import { StorageService } from "~/app/core/storage.service";
import { SettingsService } from "~/app/core/settings.service";
import { InterfaceTicketService } from "~/app/core/model/ticket-service/interface-ticket-service";
import { momentNow, toDateStr, toMoment } from "~/app/core/model/date-str";

export class SampleTicketService implements InterfaceTicketService {
    constructor(
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService
    ) {
    }

    fetchTickets(): Promise<void> {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        if (!actual.dailyTasks || !toMoment(actual.dailyTasks.date).isSame(momentNow(), "date")) {
            this.resetDailyTasks();
            // reload
            this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        }
        return Promise.resolve();
    }

    setTickets(ticketCount: number): Promise<void> {
        const tickets = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        tickets.tickets = ticketCount;
        this.storageService.storeTicketInfoActual(tickets, this.settingsService.loadSettings().env);
        return Promise.resolve();
    }

    resetDailyTasks(): Promise<void> {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        actual.dailyTasks = {
            date: toDateStr(momentNow())
        }
        actual.referralEligible = true;
        actual.referralNotified = false;
        this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
        return Promise.resolve();
    }

    spendTickets(ticketCount: number): Promise<void> {
        const tickets = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        tickets.tickets -= ticketCount;
        this.storageService.storeTicketInfoActual(tickets, this.settingsService.loadSettings().env);
        return Promise.resolve();
    }

    verifyReferral(referralCode: string): Promise<boolean> {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        if (actual.referralEligible && referralCode === "valid") {
            actual.referralEligible = false;
            const earned = this.settingsService.loadSettings().earnTicketsSignUpFromReferral;
            actual.tickets += earned;
            this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
            return Promise.resolve(true);
        } else {
            return Promise.resolve(false);
        }
    }

    earnTickets(action: "openApp"|"openOffer"|"share"|"filter"|"webVisit"|"favorite"): Promise<void> {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        switch (action) {
            case "openApp":
                if (!actual.dailyTasks.openApp) {
                    actual.dailyTasks.openApp = true;
                    actual.tickets += this.settingsService.loadSettings().earnTicketsOpenApp;
                    this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
                }
                break;
            case "openOffer":
                if (!actual.dailyTasks.openOffer) {
                    actual.dailyTasks.openOffer = true;
                    actual.tickets += this.settingsService.loadSettings().earnTicketsOpenOffer;
                    this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
                }
                break;
            case "share":
                if (!actual.dailyTasks.share) {
                    actual.dailyTasks.share = true;
                    actual.tickets += this.settingsService.loadSettings().earnTicketsShare;
                    this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
                }
                break;
            case "filter":
                if (!actual.dailyTasks.filterOffers) {
                    actual.dailyTasks.filterOffers = true;
                    actual.tickets += this.settingsService.loadSettings().earnTicketsFilterOffers;
                    this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
                }
                break;
            case "webVisit":
                if (!actual.dailyTasks.webVisit) {
                    actual.dailyTasks.webVisit = true;
                    actual.tickets += this.settingsService.loadSettings().earnTicketsVisitWebsite;
                    this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
                }
                break;
            case "favorite":
                if (!actual.dailyTasks.favorite) {
                    actual.dailyTasks.favorite = true;
                    actual.tickets += this.settingsService.loadSettings().earnTicketsFavoriteOffer;
                    this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
                }
                break;
        }
        return Promise.resolve();
    }

    earnTicketsFromRedeem(offerId: number, locationId: number, distance: number): Promise<void> {
        const actual = this.storageService.loadTicketInfoActual(this.settingsService.loadSettings().env);
        const offer = this.storageService.loadOfferInfo(offerId);
        const nearby = distance !== null && distance < this.settingsService.loadSettings().redeemNowMiles;
        if (offer) {
            if (offer.onlineOnly || !locationId || !nearby) {
                // no a specific offer location, so smaller reward
                const earned = this.settingsService.loadSettings().earnTicketsRedeemOnline;
                actual.tickets += earned;
                this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
            } else {
                // specific offer location, so full reward
                const earned = this.settingsService.loadSettings().earnTicketsRedeemLocal;
                actual.tickets += earned;
                this.storageService.storeTicketInfoActual(actual, this.settingsService.loadSettings().env);
            }
        }
        return Promise.resolve();
    }
}
